import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { BlockSides } from '../../GridDndEditor/Block/Helper/BlockSides';
import { GridBlockType } from 'services/entities/BlockTypes';
import type { SectionContentType } from '../../Sections/Section.model';
import type { TableSettingsTypes } from '../../SidePanel/content/TableSettings/types';
import type { RootState } from './Store.ts';
import { DEFAULT_SHAPE_BACKGROUND_COLOR } from '../../shared/documentColorDefaults';

interface PaddingStyleType {
  paddingTop?: number;
  paddingBottom?: number;
  paddingRight?: number;
  paddingLeft?: number;
  borderWidth?: number;
}
export enum EditorDefaultSidePanelType {
  EDITOR_DEFAULT_SIDE_PANEL = 'EDITOR_DEFAULT_SIDE_PANEL',
  DEFAULT_STYLE_SETTINGS_PANEL = 'DEFAULT_STYLE_SETTINGS_PANEL',
  TABLE_DEFAULT_STYLE_SETTINGS_PANEL = 'TABLE_DEFAULT_STYLE_SETTINGS_PANEL',
  TEXT_SNIPPET_LIBRARY_PANEL = 'TEXT_SNIPPET_LIBRARY_PANEL',
  UPLOADS_LIBRARY_PANEL = 'UPLOADS_LIBRARY_PANEL',
  FORMAT_SETTINGS_PANEL = 'FORMAT_SETTINGS_PANEL',
}

export enum ShapeType {
  CIRCLE = 'Circle',
  SQUARE = 'Square',
  TRIANGLE = 'Triangle',
}

export type SidePanelType = TableSettingsTypes | GridBlockType | EditorDefaultSidePanelType;

export interface BlockSettings {
  updatedByUserId: number;
  blockId: string;
  lockedBy?: number | null;
  borderStyle?: string;
  borderLeft?: number;
  borderRight?: number;
  borderTop?: number;
  borderBottom?: number;
  borderRadius?: number;
  borderColor?: string;
  backgroundColor?: string;
  opacity?: number;
  imageAlt?: string;
  imageLink?: string;
  paddingTop?: number;
  paddingBottom?: number;
  paddingRight?: number;
  paddingLeft?: number;
  borderWidth?: number;
  currentBorderSide?: BlockSides[];
  imageAspectRatioLock?: boolean;
  currentTimestamp?: string;
  top?: number;
}

export interface BlockStyleSettingsState {
  blockStyle: { [key: string]: BlockSettings };
  activeTotalToggle: boolean | null;
  isAdvancedSpacingModelOpen: boolean;
  isReplaceModeActive: boolean;
  isRightPanelCollapsed: boolean;
  activeSidePanel: TableSettingsTypes | GridBlockType | EditorDefaultSidePanelType;
  activeTextStyle: string | null;
  temporaryStyle: Record<
    string,
    {
      originalStyle: BlockSettings;
      dirtyStyle: BlockSettings;
    }
  >;
}

const initialState: BlockStyleSettingsState = {
  blockStyle: {},
  activeTotalToggle: null,
  isAdvancedSpacingModelOpen: false,
  isReplaceModeActive: false,
  isRightPanelCollapsed: false,
  activeSidePanel: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL,
  activeTextStyle: null,
  temporaryStyle: {},
};

const blockStyleSettingsSlice = createSlice({
  name: 'blockStyleSettings',
  initialState,
  reducers: {
    loadBlockStyleSettings: (state, action: PayloadAction<{ sections: SectionContentType[]; defaultBlockStyle: BlockSettings }>) => {
      const { sections, defaultBlockStyle } = action.payload;
      // Keep in sync with shapeDefaultStyle in hooks/useNewBlockSettings.ts.
      const shapeDefaultStyle = { ...defaultBlockStyle, backgroundColor: DEFAULT_SHAPE_BACKGROUND_COLOR, imageAspectRatioLock: false };
      sections.forEach((section) => {
        section.blocks.forEach((block) => {
          const { blockId, blockSettings, type } = block;
          const adjustedDefaultBlockStyle = type === GridBlockType.SHAPE ? shapeDefaultStyle : defaultBlockStyle;
          if (blockId && !state.blockStyle[blockId]) {
            state.blockStyle[blockId] = {
              ...adjustedDefaultBlockStyle,
              ...blockSettings,
              blockId,
              borderWidth: blockSettings?.borderLeft || 0,
              currentBorderSide: [BlockSides.ALL_SIDES],
            };
          }
        });
      });
    },
    loadImportedSectionBlockStyles: (state, action: PayloadAction<{ sections: SectionContentType[] }>) => {
      action.payload.sections.forEach((section) => {
        section.blocks.forEach((block) => {
          const { blockId, blockSettings } = block;
          if (blockId) {
            state.blockStyle[blockId] = {
              ...blockSettings,
              blockId,
              borderWidth: blockSettings?.borderLeft || 0,
              currentBorderSide: [BlockSides.ALL_SIDES],
            };
          }
        });
      });
    },
    addBlockSettings: (state, action: PayloadAction<{ blockId: string; blockSettings: BlockSettings }>) => {
      const { blockId, blockSettings } = action.payload;
      state.blockStyle[blockId] = { ...blockSettings, blockId };
    },
    addBlockToTemporaryStyle: (
      state,
      action: PayloadAction<{ blockId: string; originalStyle: BlockSettings; dirtyStyle: BlockSettings }>
    ) => {
      const { blockId, originalStyle, dirtyStyle } = action.payload;
      if (!state.temporaryStyle[blockId]) {
        state.temporaryStyle[blockId] = { originalStyle, dirtyStyle };
      } else {
        state.temporaryStyle[blockId].dirtyStyle = dirtyStyle;
      }
    },
    clearTemporaryStyleForBlock: (state, action: PayloadAction<{ blockId: string }>) => {
      const { blockId } = action.payload;
      delete state.temporaryStyle[blockId];
    },
    deleteBlockSettings: (state, action: PayloadAction<{ blockId: string }>) => {
      const { blockId } = action.payload;
      delete state.blockStyle[blockId];
    },
    updateBlockSettings: (state, action: PayloadAction<{ blockId: string; blockSettings: BlockSettings }>) => {
      const { blockId, blockSettings } = action.payload;
      if (state.blockStyle[blockId]) {
        state.blockStyle[blockId] = { ...state.blockStyle[blockId], ...blockSettings };
      }
    },
    setBlockBackgroundColor: (state, action: PayloadAction<{ blockId: string; backgroundColor: string }>) => {
      const { blockId, backgroundColor } = action.payload;
      if (state.blockStyle[blockId]) {
        state.blockStyle[blockId].backgroundColor = backgroundColor;
      }
    },
    setBlockBorderColor: (state, action: PayloadAction<{ blockId: string; borderColor: string }>) => {
      const { blockId, borderColor } = action.payload;
      if (state.blockStyle[blockId]) {
        state.blockStyle[blockId].borderColor = borderColor;
      }
    },
    setBlockBorderSide: (state, action: PayloadAction<{ blockId: string; updatedStyle: Partial<BlockSettings> }>) => {
      const { blockId, updatedStyle } = action.payload;
      if (state.blockStyle[blockId]) {
        state.blockStyle[blockId] = { ...state.blockStyle[blockId], ...updatedStyle };
      }
    },
    setBorderRadius: (state, action: PayloadAction<{ blockId: string; borderRadius: number }>) => {
      const { blockId, borderRadius } = action.payload;
      if (state.blockStyle[blockId]) {
        state.blockStyle[blockId].borderRadius = borderRadius;
      }
    },
    setBorderWidth: (state, action: PayloadAction<{ blockId: string; updatedStyle: Partial<BlockSettings> }>) => {
      const { blockId } = action.payload;
      if (state.blockStyle[blockId]) {
        state.blockStyle[blockId] = { ...state.blockStyle[blockId], ...action.payload.updatedStyle };
      }
    },
    setActiveTotalToggle: (state, action: PayloadAction<{ activeTotalToggle: boolean | null }>) => {
      state.activeTotalToggle = action.payload.activeTotalToggle;
    },
    setIsAdvancedSpacingModelOpen: (state, action: PayloadAction<boolean>) => {
      state.isAdvancedSpacingModelOpen = action.payload;
    },
    setIsReplaceModeActive: (state, action: PayloadAction<boolean>) => {
      state.isReplaceModeActive = action.payload;
    },
    setIsRightPanelCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isRightPanelCollapsed = action.payload;
      // Collapsing hides the uploads panel (see MuiEditorSidePanel's activeTabChildren gating)
      // without changing activeSidePanel, so it wouldn't otherwise be caught by the
      // setActiveSidePanel guard below. Clear replace mode here too so it can't leak into
      // a later reopen of the uploads panel.
      if (action.payload) {
        state.isReplaceModeActive = false;
      }
    },
    setPaddingValues: (state, action: PayloadAction<{ blockId: string; paddingStyle: PaddingStyleType }>) => {
      const { blockId, paddingStyle } = action.payload;
      if (state.blockStyle[blockId]) {
        state.blockStyle[blockId] = { ...state.blockStyle[blockId], ...paddingStyle };
      }
    },
    setAspectRatio: (state, action: PayloadAction<{ blockId: string; imageAspectRatioLock: boolean }>) => {
      const { blockId, imageAspectRatioLock } = action.payload;
      if (state.blockStyle[blockId]) {
        state.blockStyle[blockId].imageAspectRatioLock = imageAspectRatioLock;
      }
    },
    updateImageTop: (state, action: PayloadAction<{ blockId: string; top: number }>) => {
      const { blockId, top } = action.payload;
      if (state.blockStyle[blockId]) {
        state.blockStyle[blockId].top = top;
      }
    },
    setActiveSidePanel: (state, action: PayloadAction<{ type: SidePanelType; activeTextStyle?: string }>) => {
      state.activeSidePanel = action.payload.type;
      state.activeTextStyle = action.payload.activeTextStyle ?? null;
      if (action.payload.type !== EditorDefaultSidePanelType.UPLOADS_LIBRARY_PANEL) {
        state.isReplaceModeActive = false;
      }
    },
  },
});

export const selectBlockStyleById = (state: RootState, blockId: string) => state.blockStyleSettings.blockStyle[blockId];
export const selectIsAdvancedSpacingModelOpen = (state: RootState) => state.blockStyleSettings.isAdvancedSpacingModelOpen;
export const selectIsReplaceModeActive = (state: RootState) => state.blockStyleSettings.isReplaceModeActive;
export const selectActiveSidePanel = (state: RootState) => state.blockStyleSettings.activeSidePanel;
export const selectActiveTextStyle = (state: RootState) => state.blockStyleSettings.activeTextStyle;
export const selectBlockStyleMap = (state: RootState) => state.blockStyleSettings.blockStyle;
export const selectIsRightPanelCollapsed = (state: RootState) => state.blockStyleSettings.isRightPanelCollapsed;
export const {
  loadBlockStyleSettings,
  loadImportedSectionBlockStyles,
  addBlockSettings,
  deleteBlockSettings,
  updateBlockSettings,
  setBlockBackgroundColor,
  setBlockBorderColor,
  setActiveTotalToggle,
  setBorderRadius,
  setBlockBorderSide,
  setBorderWidth,
  setIsAdvancedSpacingModelOpen,
  setIsReplaceModeActive,
  setIsRightPanelCollapsed,
  setPaddingValues,
  setAspectRatio,
  updateImageTop,
  setActiveSidePanel,
  clearTemporaryStyleForBlock,
  addBlockToTemporaryStyle,
} = blockStyleSettingsSlice.actions;

export const blockStyleSettings = blockStyleSettingsSlice.reducer;
